import React from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

const Services = () => {
  const services = [
    {
      id: "line-of-credit",
      title: "Business Line of Credit",
      amount: "Up to $400K",
      term: "12 to 24 months",
      description:
        "A flexible revolving credit line you can draw from whenever you need it. Perfect for managing cash flow, covering unexpected expenses, or seizing new opportunities.",
      features: [
        "Draw funds anytime, 24/7",
        "Only pay interest on what you use",
        "Revolving credit — replenishes as you pay",
        "No collateral required",
      ],
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="size-8"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z"
          />
        </svg>
      ),
    },
    {
      id: "term-loans",
      title: "Term Loans",
      amount: "Up to $5M",
      term: "1 - 10 years",
      description:
        "A lump sum of capital with fixed payments over a set period. Ideal for major investments, equipment purchases, or business expansion.",
      features: [
        "Predictable fixed payments",
        "Longer repayment terms available",
        "Competitive interest rates",
        "Large funding amounts",
      ],
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="size-8"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0 0 12 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75Z"
          />
        </svg>
      ),
    },
    {
      id: "merchant-cash-advance",
      title: "Merchant Cash Advance",
      amount: "$5K - $20M",
      term: "Up to 24 months",
      description:
        "Quick funding based on your future sales. Get capital fast with flexible repayment that adjusts with your revenue.",
      features: [
        "Funding in as little as 24 hours",
        "Flexible repayment based on sales",
        "Bad credit? No problem",
        "High approval rates",
      ],
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="size-8"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
          />
        </svg>
      ),
    },
    {
      id: "debt-consolidation",
      title: "Debt Consolidation",
      amount: "Up to $3M",
      term: "Up to 24 months",
      description:
        "Simplify your finances by combining multiple business debts into one manageable payment with potentially lower rates.",
      features: [
        "One simple monthly payment",
        "Potentially lower interest rates",
        "Improve cash flow",
        "Reduce financial stress",
      ],
      icon: (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="size-8"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M7.5 21 3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"
          />
        </svg>
      ),
    },
  ];

  return (
    <>
      <SEO
        title="Funding Solutions"
        description="Explore Lloyd Funding's business financing options: lines of credit, term loans, merchant cash advances, and debt consolidation up to $20M."
        canonical="/services"
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-blue-50 to-white">
        <div className="mx-auto max-w-7xl px-4 pt-20 pb-12 sm:px-6 sm:pt-24 sm:pb-12 lg:px-8 lg:pt-24 lg:pb-16">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-3xl font-bold tracking-tight text-slate-800 sm:text-4xl lg:text-5xl">
              Our <span className="text-blue-700">Funding Solutions</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Flexible financing options designed to meet your business needs.
              From lines of credit to term loans, we have the right solution for
              you.
            </p>
          </div>
        </div>

        {/* Wave divider */}
        <div className="pointer-events-none relative h-10 overflow-hidden">
          <svg
            className="absolute bottom-0 left-0 h-16 w-full"
            viewBox="0 0 1440 120"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              d="M0,64 C288,110 576,110 864,80 C1152,50 1296,50 1440,80 L1440,120 L0,120 Z"
              fill="#F8FAFC"
            />
          </svg>
        </div>
      </section>

      {/* Services List */}
      <section className="bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
          <div className="space-y-16">
            {services.map((service, index) => (
              <div
                key={service.title}
                id={service.id}
                className="scroll-mt-24 grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center lg:gap-16"
              >
                <div
                  className={`rounded-2xl bg-white p-6 shadow-lg ring-1 ring-slate-100 lg:bg-transparent lg:p-0 lg:shadow-none lg:ring-0 ${
                    index % 2 === 1 ? "lg:order-2" : ""
                  }`}
                >
                  <div className="inline-flex rounded-2xl bg-blue-100 p-4 text-blue-600">
                    {service.icon}
                  </div>
                  <h2 className="mt-4 text-2xl font-bold text-slate-800 sm:text-3xl">
                    {service.title}
                  </h2>
                  <div className="mt-2 flex items-center gap-4">
                    <span className="text-lg font-semibold text-blue-700">
                      {service.amount}
                    </span>
                    <span className="text-slate-400">•</span>
                    <span className="text-slate-600">{service.term}</span>
                  </div>
                  <p className="mt-4 text-slate-600 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="mt-6 space-y-3">
                    {service.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3">
                        <svg
                          className="h-5 w-5 text-emerald-500 flex-shrink-0"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8">
                    <Link
                      to="/contact#application-form"
                      className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-6 py-3 text-sm font-semibold text-white shadow-sm shadow-blue-600/25 hover:bg-blue-700 hover:shadow-md hover:shadow-blue-600/30 transition-colors transition-shadow transition-[filter] duration-200 active:brightness-95"
                    >
                      Apply for {service.title}
                    </Link>
                  </div>
                </div>

                <div
                  className={`hidden lg:block rounded-2xl bg-white p-8 shadow-lg ring-1 ring-slate-100 ${
                    index % 2 === 1 ? "lg:order-1" : ""
                  }`}
                >
                  <div className="text-center">
                    <div className="inline-flex rounded-2xl bg-blue-50 p-6 text-blue-600">
                      {service.icon}
                    </div>
                    <p className="mt-4 text-3xl font-bold text-slate-800">
                      {service.amount}
                    </p>
                    <p className="mt-1 text-slate-500">{service.term}</p>
                    <div className="mt-6 border-t border-slate-100 pt-6">
                      <p className="text-sm text-slate-600">
                        Quick approval • Same-day funding available
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-800">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white sm:text-4xl">
              Not sure which option is right for you?
            </h2>
            <p className="mt-4 text-lg text-blue-100">
              Our funding experts are here to help you find the perfect
              solution.
            </p>
            <div className="mt-8">
              <Link
                to="/contact#application-form"
                className="inline-flex items-center justify-center rounded-xl bg-white px-8 py-4 sm:py-3 text-base sm:text-sm font-semibold text-blue-800 shadow-sm hover:bg-blue-50 transition-colors transition-shadow transition-[filter] duration-200 active:brightness-95"
              >
                Talk to an Expert
              </Link>
            </div>
          </div>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="bg-white">
        <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-8 lg:py-20">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-slate-800 sm:text-4xl">
              Funding Questions
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              Common questions about our funding options
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: "Which funding option is right for my business?",
                answer:
                  "It depends on your needs. Lines of credit are great for ongoing working capital, term loans work well for large one-time purchases, MCAs are ideal if you have strong daily sales, and debt consolidation helps simplify multiple existing payments.",
              },
              {
                question: "Can I combine multiple funding products?",
                answer:
                  "Yes! Many of our clients use a combination of products — for example, a term loan for equipment and a line of credit for day-to-day expenses.",
              },
              {
                question: "What's the difference between a loan and an MCA?",
                answer:
                  "A loan has fixed payments over a set term. An MCA (Merchant Cash Advance) is repaid as a percentage of your daily sales, so payments flex with your revenue.",
              },
              {
                question: "How much can I qualify for?",
                answer:
                  "Funding amounts range from $5,000 to $5 million depending on your business revenue and the funding type. Most businesses qualify for 1-2x their monthly revenue.",
              },
            ].map((faq, index) => (
              <details
                key={index}
                className="group rounded-2xl border border-slate-100 bg-slate-50 [&_summary::-webkit-details-marker]:hidden"
              >
                <summary className="flex cursor-pointer items-center justify-between gap-4 p-5 text-slate-800">
                  <h3 className="text-lg font-semibold">{faq.question}</h3>
                  <svg
                    className="size-5 shrink-0 text-blue-600 transition-transform duration-300 group-open:-rotate-180"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </summary>
                <p className="px-5 pb-5 text-slate-600 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Services;
